import {
  toastUploadError,
  toastUploadResult,
} from 'Features/Projects/Table/uploadToasts'
import {
  BaseSourceSiteWorkCentre,
  useSettingsContext,
} from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { Box } from '@mui/material'
import {
  useUpdateBaseSourceSiteWorkCentre,
  useUploadBaseSourceSiteWorkcentre,
} from '../EditSettings.data'
import { toast } from 'react-hot-toast'

interface CapacitiesTableProps {
  data: BaseSourceSiteWorkCentre[]
  isLoading: boolean
}
const onSuccess = () => {
  toast.success('Capacities saved')
}

const onError = (message?: string) => {
  toast.error(message || 'The capacities could not be saved')
}

/**
 * Head space is stored as a fraction of capacity (0.1 = 10 %): the template
 * worker plans to capacity x (1 - headSpaceBuffer). The grid shows and edits
 * it as a percentage. Downloads, uploads and bulk updates carry the stored
 * fraction. (The grid used to show the fraction with a % sign, so 10 typed
 * for 10 % planned to a negative capacity.)
 */
const toPercent = (fraction: unknown) =>
  fraction === null || fraction === undefined || fraction === ''
    ? null
    : Math.round(Number(fraction) * 100 * 1e6) / 1e6
export const CapacitiesTable = ({ data, isLoading }: CapacitiesTableProps) => {
  const COLUMN_WIDTH = 150

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const { mutate, isPending: isSaving } = useUpdateBaseSourceSiteWorkCentre(
    onSuccess,
    onError
  )
  const onUploadSuccess = toastUploadResult
  const onUploadError = toastUploadError
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadBaseSourceSiteWorkcentre(onUploadSuccess, onUploadError)

  const updateTableData = (
    changedRows: Record<string, BaseSourceSiteWorkCentre>
  ) => {
    mutate({
      changedRows,
      projectId,
      scenarioId,
    })
  }

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        flex: 1,
        pinned: true,
      },
      {
        field: 'workcentreCode',
        headerName: 'Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
        flex: 1,
        pinned: true,
      },
      {
        field: 'workcentreCapacity',
        headerName: 'Capacity (h / week)',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        flex: 1,
        valueFormatter: (value: number) => {
          if (value === null || value === undefined) return ''
          return value.toFixed(1)
        },
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !(Number(params.props.value) >= 0),
        }),
      },
      {
        field: 'headSpaceBuffer',
        headerName: 'Head space (%)',
        description:
          'Capacity held back from the templates: they plan to capacity × (1 − head space). Stored as a fraction (0.1 = 10 %) in downloads and uploads.',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        flex: 1,
        valueGetter: (value: unknown) => toPercent(value),
        valueSetter: (value: unknown, row: BaseSourceSiteWorkCentre) => ({
          ...row,
          headSpaceBuffer:
            value === null || value === undefined || value === ''
              ? 0
              : Number(value) / 100,
        }),
        valueFormatter: (value: number | null) => {
          if (value === null || value === undefined) return ''
          return `${value.toFixed(1)} %`
        },
        preProcessEditCellProps: (params) => {
          const percent = Number(params.props.value)
          return {
            ...params.props,
            error:
              params.props.value !== '' &&
              params.props.value !== null &&
              !(percent >= 0 && percent < 100),
          }
        },
      },
    ]
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={(file: File) => {
          uploadMutate({ file, projectId, scenarioId })
        }}
        isUploading={isUploading ?? false}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={['baseSourceSiteCode', 'workcentreCode']}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
